import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import apiService from '../../services/api';
import { User } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';

interface AdminDataDeletionTabProps {
  user: User;
  onDeleted: () => void;
}

const AdminDataDeletionTab: React.FC<AdminDataDeletionTabProps> = ({ user, onDeleted }) => {
  const { notify } = useNotification();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      await apiService.deleteUser(user.id);
      notify({
        message: `${user.name || user.email} deleted successfully.`,
        severity: 'success',
      });
      setConfirmOpen(false);
      onDeleted();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete user.';
      setError(message);
      notify({ message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity="warning">
        <Typography variant="body2">
          <strong>Warning:</strong> Deleting this user will permanently remove all their data including profile,
          bookings, documents, and activity logs. This action cannot be undone.
        </Typography>
      </Alert>

      <Stack spacing={1}>
        <Typography variant="body1">
          User: <strong>{user.name || user.email}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Email: {user.email}
        </Typography>
      </Stack>

      <Button
        variant="contained"
        color="error"
        startIcon={<DeleteForever />}
        onClick={() => setConfirmOpen(true)}
      >
        Delete user permanently
      </Button>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!loading) {
            setConfirmOpen(false);
            setError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Data Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to permanently delete <strong>{user.name || user.email}</strong>. This cannot be undone.
            All their personal data, documents, and related resources will be removed from the platform.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForever />}
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDataDeletionTab;
